import { FileUpload } from '@consigli/facade';
import { ExtendedFile } from '@consigli/hooks';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SelectVersionPackage } from './select-version-package';
import { VERSION_COMPARE_SOURCE_TYPES } from './version-compare-wizard';

export enum PackageInputMode {
  SELECT_EXISTING = 'SELECT_EXISTING',
  UPLOAD_NEW = 'UPLOAD_NEW',
}

interface VersionPackageSelectorProps {
  /** react-hook-form field name for the resolved package ID (used in SELECT_EXISTING mode) */
  fieldName: string;
  label: string;
  mode: PackageInputMode;
  onModeChange: (mode: PackageInputMode) => void;
  files: ExtendedFile[];
  onFilesChange: Dispatch<SetStateAction<ExtendedFile[]>>;
}

const MAX_FILE_SIZE_BYTES = 600 * 1024 * 1024;

export const VersionPackageSelector: FC<VersionPackageSelectorProps> = ({
  fieldName,
  label,
  mode,
  onModeChange,
  files,
  onFilesChange,
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<FieldValues>();
  const [showMessagePanel, setShowMessagePanel] = useState(true);

  const handleModeChange = (next: PackageInputMode) => {
    onModeChange(next);
    setValue(fieldName, null);
  };

  const tabBase =
    'min-w-[7.5rem] whitespace-nowrap text-center py-1.5 text-sm font-medium rounded transition-colors focus:outline-none';
  const activeTab = `${tabBase} bg-white text-day-neutral-dark shadow-sm`;
  const inactiveTab = `${tabBase} text-day-neutral-subtle hover:text-day-neutral-dark`;

  return (
    <div className="min-w-0">
      <div className="mt-4 mb-4 flex items-center justify-between">
        <p className="text-base font-semibold text-day-neutral-subtle">{label}</p>
        <div className="flex gap-1 rounded bg-day-light-3 p-0.5">
          <button
            type="button"
            className={mode === PackageInputMode.SELECT_EXISTING ? activeTab : inactiveTab}
            onClick={() => handleModeChange(PackageInputMode.SELECT_EXISTING)}
          >
            {t('version-compare.select-existing')}
          </button>
          <button
            type="button"
            className={mode === PackageInputMode.UPLOAD_NEW ? activeTab : inactiveTab}
            onClick={() => handleModeChange(PackageInputMode.UPLOAD_NEW)}
          >
            {t('version-compare.upload-new')}
          </button>
        </div>
      </div>

      {mode === PackageInputMode.SELECT_EXISTING ? (
        <SelectVersionPackage
          fieldName={fieldName}
          label=""
          placeholder={t('version-compare.select-existing-placeholder')}
          sourceTypes={VERSION_COMPARE_SOURCE_TYPES}
        />
      ) : (
        <FileUpload
          files={files}
          setFiles={onFilesChange as Dispatch<SetStateAction<File[]>>}
          dropAreaLabel={t('document-upload.drag-n-drop-here')}
          dropAreaClassName="h-36 w-full"
          fileButtonLabel={t('document-upload.browse-files')}
          folderButtonLabel={t('document-upload.browse-folders')}
          uploadListLabel={t('document-upload.files')}
          zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
          showMessagePanel={showMessagePanel}
          setShowMessagePanel={setShowMessagePanel}
          maxFileSizeBytes={MAX_FILE_SIZE_BYTES}
          fileSizeWarningText={t('document-upload.file-exceeds-max-size')}
        />
      )}
    </div>
  );
};
